import React from 'react';

interface InventoryAlertsProps {
  inventoryStatus: any;
  userRole: string;
}

const InventoryAlerts: React.FC<InventoryAlertsProps> = ({ inventoryStatus, userRole }) => {
  if (userRole !== 'manager' && userRole !== 'executive2') {
    return null;
  }

  const lowStockAlerts = inventoryStatus?.low_stock_alerts || [];
  const binUtilization = inventoryStatus?.bin_utilization || 0;
  const rackUtilization = inventoryStatus?.rack_utilization || 0;

  const getUtilizationColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600 bg-red-50';
    if (percentage >= 75) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Inventory Alerts</h3>
      </div>
      <div className="p-6 space-y-6">
        {/* Storage Utilization */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Storage Utilization</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Bin Utilization</span>
              <span className={`text-sm font-medium px-2 py-1 rounded ${getUtilizationColor(binUtilization)}`}>
                {binUtilization.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  binUtilization >= 90 ? 'bg-red-500' : 
                  binUtilization >= 75 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(binUtilization, 100)}%` }}
              />
            </div>
          </div>
          
          <div className="space-y-3 mt-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Rack Utilization</span>
              <span className={`text-sm font-medium px-2 py-1 rounded ${getUtilizationColor(rackUtilization)}`}>
                {rackUtilization.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  rackUtilization >= 90 ? 'bg-red-500' : 
                  rackUtilization >= 75 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(rackUtilization, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Low Stock Alerts */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Low Stock Alerts ({lowStockAlerts.length})
          </h4>
          {lowStockAlerts.length > 0 ? (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {lowStockAlerts.slice(0, 10).map((alert: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-900">
                      {alert.sku || `${alert.style_code}-${alert.size}-${alert.color}`}
                    </p>
                    <p className="text-xs text-red-700">
                      Current: {alert.current_quantity || 0} | Threshold: {alert.threshold || 5}
                    </p>
                  </div>
                  <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
                    Low Stock
                  </span>
                </div>
              ))}
              {lowStockAlerts.length > 10 && (
                <p className="text-sm text-gray-500 text-center py-2">
                  +{lowStockAlerts.length - 10} more alerts
                </p>
              )}
            </div>          ) : (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-green-600 font-bold text-lg">✓</span>
              </div>
              <p className="text-sm text-gray-500">No low stock alerts</p>
            </div>
          )}
        </div>

        {/* System Health */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">System Health</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2">
              <span className="text-sm text-gray-600">Inventory Sync</span>
              <div className="flex items-center">
                <div className="h-2 w-2 bg-green-400 rounded-full mr-2"></div>
                <span className="text-sm text-green-600">Active</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-2">
              <span className="text-sm text-gray-600">Order Processing</span>
              <div className="flex items-center">
                <div className="h-2 w-2 bg-green-400 rounded-full mr-2"></div>
                <span className="text-sm text-green-600">Normal</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-2">
              <span className="text-sm text-gray-600">Database</span>
              <div className="flex items-center">
                <div className="h-2 w-2 bg-green-400 rounded-full mr-2"></div>
                <span className="text-sm text-green-600">Connected</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryAlerts;
